import React, { useState, useEffect } from "react";
import { Container, DropZone, Legend, Files } from "./styles";
import { useDropzone } from "react-dropzone";
import { SectionTitle } from "App/styles";
import { ErrorMessage, FieldLabel } from "basics/styles";
import ProgressBar from "components/ProgressBar";
import useFiles from "hooks/useStorage";
import UploadIcon from "icons/UploadIcon";

export default function FileUploader({
  value,
  label,
  title,
  required,
  folder,
  id,
  register,
  setValue,
  error,
  accepted,
  mode,
  readOnly,
  multiple,
  onPendingChange,
}) {
  const [files, setFiles] = useState([]);
  const [uploadError, setUploadError] = useState("");

  const { uploadFile } = useFiles();

  const { getRootProps, getInputProps } = useDropzone({
    accept: accepted,
    disabled: readOnly,
    onDropRejected: (rejections) => {
      // react-dropzone descarta acá los archivos que no matchean `accepted`.
      // Antes se ignoraban en silencio: el usuario creía haber subido el
      // archivo y en realidad no se agregaba nada.
      const names = rejections.map((r) => r.file.name).join(", ");
      setUploadError(
        `Archivo no permitido${names ? ` (${names})` : ""}. ` +
          `Solo se aceptan archivos ${accepted || "válidos"}.`
      );
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setUploadError("");
      }

      acceptedFiles.forEach((file) => {
        setFiles((prevFiles) => [
          ...prevFiles,
          { name: file.name, url: "", progress: 0 },
        ]);

        const task = uploadFile(folder, file, file.name);

        const onProgress = (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            0
          );

          setFiles((prevFiles) => {
            return prevFiles.map((attachment) =>
              attachment.name === file.name
                ? { ...attachment, progress: progress }
                : attachment
            );
          });
        };
        const onError = () => {
          // La subida falló: sacamos el archivo a medio subir (url vacía) para
          // no dejar el form bloqueado ni guardar un adjunto sin URL.
          setUploadError(
            `No se pudo subir "${file.name}". Intente nuevamente.`
          );
          setFiles((prevFiles) =>
            prevFiles.filter((attachment) => attachment.name !== file.name)
          );
        };

        const onComplete = () => {
          task.snapshot.ref.getDownloadURL().then((url) => {
            setFiles((prevFiles) => {
              return prevFiles.map((file) =>
                file.name === task.snapshot.metadata.name
                  ? { ...file, progress: 100, url: url }
                  : file
              );
            });
          });
        };

        task.on("state_changed", onProgress, onError, onComplete);
      });
    },
  });

  const onRemoveFile = (name) => {
    setFiles(
      files.filter(function (f) {
        return f.name !== name;
      })
    );
  };

  useEffect(() => {
    setFiles(value || []);
  }, [value]);

  useEffect(() => {
    setValue(
      id,
      files.map((file) => {
        return { name: file.name, url: file.url };
      })
    );
  }, [files, setValue, id]);

  useEffect(() => {
    register({ name: id });

    setValue(id, value || []);
  }, [register, setValue, id, value]);

  // Un archivo con `url` vacía es uno que todavía se está subiendo (o cuyo
  // getDownloadURL aún no resolvió). Mientras haya alguno pendiente avisamos al
  // form para que deshabilite Guardar y no se persista un adjunto sin URL.
  useEffect(() => {
    if (!onPendingChange) return;

    const pending = files.some((file) => !file.url);
    onPendingChange(id, pending);
  }, [files, onPendingChange, id]);

  // Al desmontar (cierre del modal) liberamos el "pendiente" para no dejar el
  // botón Guardar trabado en el próximo formulario.
  useEffect(() => {
    return () => {
      onPendingChange && onPendingChange(id, false);
    };
  }, [onPendingChange, id]);

  return (
    (!readOnly || files.length > 0) && (
      <Container>
        {title && <SectionTitle>{title}</SectionTitle>}
        {label && (
          <FieldLabel mode={mode} htmlFor={id} required={required}>
            {label}
          </FieldLabel>
        )}

        {!readOnly && (
          <DropZone mode={mode} {...getRootProps({ className: "dropzone" })}>
            <input
              type="file"
              name={id}
              {...getInputProps()}
              multiple={multiple}
            />
            <UploadIcon style={{ width: "3.2rem", height: "3.2rem" }} />
            <Legend mode={mode}>
              Arrastrá los archivos acá o hacé clic para elegirlos
              <em>Se suben apenas los soltás, no hace falta confirmar.</em>
            </Legend>
          </DropZone>
        )}
        <Files>
          {files.map((file) => {
            return (
              <ProgressBar
                key={file.name}
                readOnly={readOnly}
                url={file.url}
                percent={file.progress || (readOnly ? 100 : 0)}
                name={file.name}
                mode={mode}
                onRemoveFile={onRemoveFile}
              />
            );
          })}
        </Files>
        {uploadError && <ErrorMessage mode={mode}>{uploadError}</ErrorMessage>}
        {error && <ErrorMessage mode={mode}>{error.message}</ErrorMessage>}
      </Container>
    )
  );
}
